<template>
  <remote-multiselect
    v-bind="$attrs"
    ref="multiselect"
    class="job-multiselect"
    v-model="forwardValue"
    :id="contextId"
    label="name"
    track-by="id"
    :placeholder="placeholder"
    select-label=""
    deselect-label=""
    resourceName="Job"
    :serviceFetch="serviceFetch || fetchJobs"
    :multiple="multiple"
    :disabled="disabled"
    :showAddNewItem="!!(showAddNewItem && canEditCosting)"
    :showMagnifyingGlass="showMagnifyingGlass"
    :modalComponent="async () => (await import('@/modules/costing/JobFormModal.vue')).default"
    @item-added="onItemAdded"
    @magnifying-glass-click="openItem"
    @fullValueChange="$emit('fullValueChange', $event)"
  >
    <template #beforeList2 v-if="activeOption">
      <div class="show-inactive">
        <b-form-checkbox
          v-model="includeInactive"
          ref="checkbox"
          @change="onChangeCheckbox"
        >
          Include inactive jobs
        </b-form-checkbox>
      </div>
    </template>
  </remote-multiselect>
</template>

<script setup>
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useStore } from 'vuex'
import RemoteMultiselect from '@/components/RemoteMultiselect.vue'
import JobService from '@/modules/costing/services/JobService'
import { openJobFormModal } from '@/modules/costing/openJobFormModal'
import { useBootstrapVueModal } from '@/composables/useBootstrapVueModal'

defineOptions({
  name: 'JobMultiselect',
  inheritAttrs: false
})
const props = defineProps({
  contextId: {},
  modelValue: [Number, Array],
  placeholder: {
    type: String,
    default: 'Select a job',
  },
  multiple: Boolean,
  disabled: Boolean,
  activeOption: Boolean,
  showAddNewItem: Boolean,
  showMagnifyingGlass: Boolean,
  // allows overriding the default fetchJobs function, which is useful for costing selection
  // where we have more complex filtering.
  // TODO: Can we merge that into this component itself?
  serviceFetch: Function
})
const emit = defineEmits(['fullValueChange', 'update:modelValue'])

const multiselectRef = useTemplateRef('multiselect')
const modalController = useBootstrapVueModal()
const includeInactive = ref(false)
const forwardValue = computed({
  get() {
    return props.modelValue
  },
  set(v) {
    emit('update:modelValue', v)
  },
})

const store = useStore()
const canEditCosting = computed(() => store.getters['canEditCosting'])

watch(
  includeInactive,
  () => {
    // refetch jobs since filter changed
    multiselectRef.value?.trySearch?.()
  }
)

function fetchJobs(searchText, value, limit) {
  const active =
    value || (props.activeOption && includeInactive.value) ? 'all' : 'active'
  return JobService.list({
    searchText,
    active,
    id: value,
    limit,
  }).then(data => data?.results || [])
}

function onChangeCheckbox() {
  // Prevent multiselect from closing after clicking on checkbox.
  // I couldn't figure out how to block the blur event in the first place.
  multiselectRef.value.$refs.select.$refs.select.activate()
}

function onItemAdded (item) {
  const itemId = ref(null)
    itemId.value = item.id
    if (props.multiple) {
    if (!props.modelValue.includes(item.id)) {
      emit('update:modelValue', props.modelValue.concat(item.id))
      emit('item-added', item)
    }
    } else {
    if (props.modelValue !== item.id) {
      emit('update:modelValue', item.id)
      emit('item-added', item)
    }
  }
}

function openItem (item) {
  openJobFormModal(item.id, modalController.showModal)
}

</script>

<style scoped>
.job-multiselect {
  .show-inactive {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    /* Opaque background needed since it's sticky above scrollable option rows. */
    background-color: var(--color-slate-900);
  }
}
</style>
